import { useCallback, useEffect, useRef, useState } from 'react';

export type VideoRecorderError =
  | 'CAMERA_ACCESS_DENIED'
  | 'CAMERA_NOT_INITIALIZED'
  | 'RECORDING_FAILED'
  | 'DEVICE_ENUMERATION_FAILED';

type FacingMode = 'user' | 'environment';

interface UseVideoRecorderOptions {
  /** Maximum recording duration in seconds (default: 30) */
  maxDuration?: number;
  /** Initial camera facing mode (default: 'environment') */
  initialFacingMode?: FacingMode;
  /** Called when recording auto-stops because maxDuration was reached. */
  onAutoStop?: (file: File) => void;
}

interface UseVideoRecorderReturn {
  /** Whether the camera is being initialized */
  isInitializing: boolean;
  /** Whether the camera is ready for recording */
  isCameraReady: boolean;
  /** Whether recording is in progress */
  isRecording: boolean;
  /** Current recording duration in seconds */
  recordingTime: number;
  /** Active media stream (for preview) */
  stream: MediaStream | null;
  /** Error code if something went wrong */
  error: VideoRecorderError | null;
  /** Current camera facing mode */
  facingMode: FacingMode;
  /** Whether device has multiple cameras */
  hasMultipleCameras: boolean;
  /** Initialize camera and request permissions */
  initializeCamera: () => Promise<void>;
  /** Start recording video */
  startRecording: () => void;
  /** Stop recording and return the video file */
  stopRecording: () => Promise<File | null>;
  /** Cancel recording and cleanup */
  cancelRecording: () => void;
  /** Stop camera and cleanup all resources */
  cleanup: () => void;
  /** Switch between front and back cameras */
  switchCamera: () => Promise<void>;
}

const MIME_TYPE_PREFERENCES = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'] as const;

const getSupportedMimeType = (): string => {
  for (const mimeType of MIME_TYPE_PREFERENCES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }
  return 'video/webm'; // fallback
};

const getFileExtension = (mimeType: string): string => {
  return mimeType.includes('mp4') ? 'mp4' : 'webm';
};

export const useVideoRecorder = ({
  maxDuration = 30,
  initialFacingMode = 'environment',
  onAutoStop,
}: UseVideoRecorderOptions = {}): UseVideoRecorderReturn => {
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<VideoRecorderError | null>(null);
  const [facingMode, setFacingMode] = useState<FacingMode>(initialFacingMode);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mimeTypeRef = useRef<string>('video/webm');
  const streamRef = useRef<MediaStream | null>(null);
  // Resolver set by stopRecording() while waiting for onstop. If null when onstop
  // fires, the stop came from auto-stop and onAutoStopRef is invoked instead.
  const stopResolverRef = useRef<((file: File | null) => void) | null>(null);
  const onAutoStopRef = useRef<typeof onAutoStop>(onAutoStop);

  useEffect(() => {
    onAutoStopRef.current = onAutoStop;
  }, [onAutoStop]);

  // Check for multiple cameras on mount
  useEffect(() => {
    const checkMultipleCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch {
        setHasMultipleCameras(false);
      }
    };
    checkMultipleCameras();
  }, []);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  const stopStreamTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    }
  }, []);

  const cleanup = useCallback(() => {
    clearTimers();
    stopStreamTracks();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    stopResolverRef.current = null;
    setRecordingTime(0);
    setIsRecording(false);
    setIsCameraReady(false);
    setIsInitializing(false);
  }, [clearTimers, stopStreamTracks]);

  const initializeCameraWithFacingMode = useCallback(
    async (mode: FacingMode) => {
      try {
        setError(null);
        setIsInitializing(true);
        chunksRef.current = [];

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: mode },
          audio: true,
        });

        streamRef.current = mediaStream;
        setStream(mediaStream);

        mimeTypeRef.current = getSupportedMimeType();

        setIsCameraReady(true);
        setIsInitializing(false);
      } catch {
        setError('CAMERA_ACCESS_DENIED');
        setIsInitializing(false);
        cleanup();
      }
    },
    [cleanup]
  );

  const initializeCamera = useCallback(async () => {
    await initializeCameraWithFacingMode(facingMode);
  }, [initializeCameraWithFacingMode, facingMode]);

  const switchCamera = useCallback(async () => {
    stopStreamTracks();
    setIsCameraReady(false);

    const newFacingMode: FacingMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacingMode);

    // Small delay for mobile browsers to release camera
    await new Promise((resolve) => setTimeout(resolve, 100));

    await initializeCameraWithFacingMode(newFacingMode);
  }, [facingMode, initializeCameraWithFacingMode, stopStreamTracks]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) {
      setError('CAMERA_NOT_INITIALIZED');
      return;
    }

    try {
      chunksRef.current = [];
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: mimeTypeRef.current,
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      // Register onstop once: handles both manual stop (resolver waiting) and
      // auto-stop (no resolver — emit via onAutoStop callback). Without this,
      // auto-stop produced no file and the consumer never transitioned to preview.
      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'video/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const extension = getFileExtension(mimeType);
        const file = new File([blob], `video-${Date.now()}.${extension}`, { type: mimeType });

        clearTimers();
        setIsRecording(false);

        const resolver = stopResolverRef.current;
        stopResolverRef.current = null;

        if (resolver) {
          resolver(file);
        } else {
          onAutoStopRef.current?.(file);
        }
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);

      // Start the timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      // Auto-stop at max duration
      autoStopTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
      }, maxDuration * 1000);
    } catch {
      setError('RECORDING_FAILED');
    }
  }, [clearTimers, maxDuration]);

  const stopRecording = useCallback(async (): Promise<File | null> => {
    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder || mediaRecorder.state !== 'recording') {
        clearTimers();
        setIsRecording(false);
        resolve(null);
        return;
      }

      stopResolverRef.current = resolve;
      mediaRecorder.stop();
    });
  }, [clearTimers]);

  const cancelRecording = useCallback(() => {
    const mediaRecorder = mediaRecorderRef.current;
    // Detach onstop so the cancel doesn't fire onAutoStop or resolve a stale Promise.
    if (mediaRecorder) {
      mediaRecorder.onstop = null;
      if (mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    }
    if (stopResolverRef.current) {
      stopResolverRef.current(null);
      stopResolverRef.current = null;
    }
    cleanup();
  }, [cleanup]);

  return {
    isInitializing,
    isCameraReady,
    isRecording,
    recordingTime,
    stream,
    error,
    facingMode,
    hasMultipleCameras,
    initializeCamera,
    startRecording,
    stopRecording,
    cancelRecording,
    cleanup,
    switchCamera,
  };
};
